/**
 * RBAC Phase 3 — chained orchestrator operator sign-in for `VITE_ADMIN_AUTH=oidc` (pure helpers).
 *
 * The orchestrator-backed pages need the orchestrator operator cookie session. In OIDC mode the
 * SPA gets one by a top-level navigation to the orchestrator's operator OAuth start
 * (`/auth/oauth/start?app=operator&returnTo=<abs SPA url>`, no provider). auth-server already
 * holds the MFA'd browser session, so it answers immediately and the orchestrator mints an MFA
 * operator session from the verified access token, then redirects to `returnTo`.
 */

/** One chained attempt per window (and per page load). */
export const ORCHESTRATOR_CHAIN_WINDOW_MS = 2 * 60 * 1000;

export function buildOrchestratorOperatorStartUrl(orchestratorV1Base: string, returnUrl: string): string {
  const url = new URL(`${orchestratorV1Base.replace(/\/+$/, '')}/auth/oauth/start`);
  url.searchParams.set('app', 'operator');
  url.searchParams.set('returnTo', returnUrl);
  return url.toString();
}

export type OrchestratorChainDecision =
  /** navigate to the orchestrator operator OAuth start now */
  | 'start'
  /** nothing to do (session present, pending MFA, still probing, not applicable) */
  | 'none'
  /** a chained attempt already ran and there is still no session: show the notice */
  | 'unavailable';

export function decideOrchestratorChain(input: {
  oidcMode: boolean;
  orchestratorDisabled: boolean;
  /** orchestrator `/auth/me` result; undefined while probing / on a transport error */
  session: { authenticated?: boolean; mfaChallengePending?: boolean } | undefined;
  /** the admin-api session is up (OIDC token works) — never chain before that */
  adminSessionReady: boolean;
  attemptedThisPageLoad: boolean;
  lastAttemptAtMs: number | null;
  nowMs: number;
}): OrchestratorChainDecision {
  if (!input.oidcMode || input.orchestratorDisabled || !input.adminSessionReady) return 'none';
  const s = input.session;
  if (!s || s.authenticated === true || s.mfaChallengePending === true) return 'none';
  const recent =
    input.lastAttemptAtMs !== null &&
    input.nowMs - input.lastAttemptAtMs >= 0 &&
    input.nowMs - input.lastAttemptAtMs < ORCHESTRATOR_CHAIN_WINDOW_MS;
  if (input.attemptedThisPageLoad || recent) return 'unavailable';
  return 'start';
}
